import React, { useState, useEffect } from "react";
import { CreatExBankAccounts } from "../auth/stripe";
import { REACT_PK } from "../../config";
import { loadStripe } from "@stripe/stripe-js";
import { getElakayLanguageContext } from "./dataElakay";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["formEBC"]?.[key] || fallback;

export const BANK_CONFIG = {
  US: {
    country: "US",
    country_name: "United States",
    currency: "usd",
    currency_name: "US Dollar",
    routing_number_format: "9 digits",
    account_number_format: "4–17 digits",
    examples: {
      routing_number: "110000000", // Stripe test
      account_number: "000123456789", // Stripe test
    },
    banks: [
      "Chase Bank",
      "Bank of America",
      "Wells Fargo",
      "Citibank",
      "Capital One",
      "US Bank"
    ],
    notes:
      "US bank accounts require a 9-digit routing number and a valid account number. The routing number identifies the bank; the account number identifies the individual account."
  },

  CA: {
    country: "CA",
    country_name: "Canada",
    currency: "cad",
    currency_name: "Canadian Dollar",
    routing_number_format: "Transit (5 digits) + Institution (3 digits)",
    account_number_format: "7–12 digits selon la banque",
    examples: {
      transit_number: "00000",
      institution_number: "000",
      account_number: "0000000"
    },
    banks: [
      "Royal Bank of Canada (RBC)",
      "TD Canada Trust",
      "Scotiabank",
      "Bank of Montreal (BMO)",
      "CIBC",
      "National Bank of Canada"
    ],
    notes:
      "Au Canada, le routing number est composé du transit number (5 chiffres) suivi du institution number (3 chiffres). L’account number dépend de la banque."
  }
};

const styles = {
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
    border: "1px solid #e2e8f0",
    fontFamily: "inherit",
  },
  headerRow: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" },
  iconBadge: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #007399 0%, #00a4d8 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: "18px", fontWeight: 700, color: "#0f172a" },
  subtitle: { margin: 0, fontSize: "13px", color: "#64748b" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" },
  field: { marginBottom: "16px" },
  label: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "6px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "14px",
    outline: "none",
  },
  hint: { fontSize: "12px", color: "#94a3b8", marginTop: "4px", display: "block" },
  alertError: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
    color: "#b91c1c",
    borderRadius: "10px",
    padding: "10px 14px",
    marginBottom: "16px",
    fontSize: "13px",
  },
  actions: { display: "flex", justifyContent: "space-between", marginTop: "10px", gap: "10px" },
  cancelBtn: {
    padding: "10px 20px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "white",
    color: "#334155",
    fontWeight: 600,
    cursor: "pointer",
  },
  saveBtn: {
    padding: "10px 24px",
    borderRadius: "10px",
    border: "none",
    background: "linear-gradient(135deg, #007399 0%, #00a4d8 100%)",
    color: "white",
    fontWeight: 700,
    cursor: "pointer",
  },
  saveBtnDisabled: {
    padding: "10px 24px",
    borderRadius: "10px",
    border: "none",
    background: "#cbd5e1",
    color: "white",
    fontWeight: 700,
    cursor: "not-allowed",
  },
};

const EMPTY_VALUES = {
  account_holder_name: "",
  account_holder_type: "individual",
  routing_number: "",
  account_number: "",
  ConAccount_number: "",
  error: "",
  success: false,
};

function validateBankFields(values) {
  for (const key of ["account_holder_name", "account_holder_type", "routing_number", "account_number", "ConAccount_number"]) {
    if (!values[key]) return false;
  }
  return values.account_number === values.ConAccount_number;
}

// Module scope, not inside the component: loadStripe() must be called
// once and reused (React Stripe.js docs) -- calling it on every render,
// as this used to, hands <Elements> a new stripePromise reference each
// time, which resets its internal state. Same pattern already used
// correctly by wallet.js/CheckoutProd.js/InvestmentSections.jsx.
const stripePromise = loadStripe(REACT_PK);

const FormBankCountry = ({
  countrySel = "US",
  setAddExBankAcc,
  isAuthenticated,
  setcountrySel,
  infoSource,
  onSuccess,
}) => {
  __setElakayInfoSource(infoSource);
  const { token } = isAuthenticated();

  const cfg = BANK_CONFIG[(countrySel || "US").toUpperCase()] || BANK_CONFIG.US;
  const isCA = cfg.country === "CA";

  const [transitNumber, setTransitNumber] = useState("");
  const [institutionNumber, setInstitutionNumber] = useState("");
  const [loadingB, setLoadingB] = useState(false);
  const [values, setValues] = useState(EMPTY_VALUES);

  const {
    account_holder_name,
    account_holder_type,
    routing_number,
    account_number,
    ConAccount_number,
    error,
  } = values;

  useEffect(() => {
    setValues(EMPTY_VALUES);
    setTransitNumber("");
    setInstitutionNumber("");
  }, [countrySel]);

  useEffect(() => {
    if (isCA) {
      setValues((prev) => ({ ...prev, routing_number: `${transitNumber}${institutionNumber}` }));
    }
  }, [transitNumber, institutionNumber, isCA]);

  const handleChange = (name) => (event) => {
    setValues({ ...values, error: "", [name]: event.target.value });
  };

  const isValid = validateBankFields(values);

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoadingB(true);

    try {
      const stripe = await stripePromise;
      const result = await stripe.createToken("bank_account", {
        country: cfg.country,
        currency: cfg.currency,
        routing_number,
        account_number,
        account_holder_name,
        account_holder_type,
      });

      if (result.error) {
        setValues((prev) => ({ ...prev, error: result.error.message, success: false }));
        setLoadingB(false);
        return;
      }

      const data = await CreatExBankAccounts(token, { token: result.token.id, set_default: false });

      if (data.error) {
        setValues((prev) => ({ ...prev, error: data.error, success: false }));
        setLoadingB(false);
        return;
      }

      setValues({ ...EMPTY_VALUES, success: true });
      setcountrySel("Selected");
      setAddExBankAcc(false);
      onSuccess?.();
    } catch (err) {
      setValues((prev) => ({ ...prev, error: err.message, success: false }));
    } finally {
      setLoadingB(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.iconBadge}>🏦</div>
        <div>
          <h3 style={styles.title}>{cfg.country_name} {__elakayText("t005", "Payout Bank Account")}</h3>
          <p style={styles.subtitle}>{__elakayText("t006", "Link a bank account to receive your payouts")}</p>
        </div>
      </div>

      {error ? <div style={styles.alertError}>{error}</div> : null}

      <div style={styles.field}>
        <label style={styles.label}>{infoSource.laguangeElakay('label', infoSource.languageAPI).accountName}</label>
        <input
          style={styles.input}
          onChange={handleChange('account_holder_name')}
          type="text"
          value={account_holder_name}
        />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>{infoSource.laguangeElakay('label', infoSource.languageAPI).accountType}</label>
        <select style={styles.input} onChange={handleChange('account_holder_type')} value={account_holder_type}>
          <option value="individual">{infoSource.laguangeElakay('label', infoSource.languageAPI).individual}</option>
          <option value="company">{infoSource.laguangeElakay('label', infoSource.languageAPI).company}</option>
        </select>
      </div>

      {isCA ? (
        <div style={styles.field}>
          <label style={styles.label}>{infoSource.laguangeElakay('label', infoSource.languageAPI).routingNumber}</label>
          <div style={styles.grid}>
            <input
              style={styles.input}
              maxLength={5}
              placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).formEBC.t001}
              value={transitNumber}
              onChange={(e) => setTransitNumber(e.target.value.replace(/\D/g, ""))}
            />
            <input
              style={styles.input}
              maxLength={3}
              placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).formEBC.t002}
              value={institutionNumber}
              onChange={(e) => setInstitutionNumber(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <span style={styles.hint}>{cfg.routing_number_format}</span>
        </div>
      ) : (
        <div style={styles.field}>
          <label style={styles.label}>{infoSource.laguangeElakay('label', infoSource.languageAPI).routingNumber}</label>
          <input
            style={styles.input}
            maxLength={9}
            onChange={handleChange('routing_number')}
            type="text"
            value={routing_number}
          />
          <span style={styles.hint}>{cfg.routing_number_format}</span>
        </div>
      )}

      <div style={styles.grid}>
        <div style={styles.field}>
          <label style={styles.label}>{infoSource.laguangeElakay('label', infoSource.languageAPI).accountNumber}</label>
          <input style={styles.input} onChange={handleChange('account_number')} type="text" value={account_number} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>{infoSource.laguangeElakay('label', infoSource.languageAPI).confAccountNumber}</label>
          <input
            style={styles.input}
            onChange={handleChange('ConAccount_number')}
            type="text"
            value={ConAccount_number}
          />
        </div>
      </div>

      <div style={styles.actions}>
        <button type="button" style={styles.cancelBtn} onClick={() => setAddExBankAcc(false)}>
          {infoSource.laguangeElakay('Button', infoSource.languageAPI).cancel}
        </button>
        <button
          type="button"
          style={isValid && !loadingB ? styles.saveBtn : styles.saveBtnDisabled}
          disabled={!isValid || loadingB}
          onClick={handleSubmit}
        >
          {loadingB ? (
            <>
              <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
              {infoSource.laguangeElakay('title', infoSource.languageAPI).loanding}
            </>
          ) : (
            infoSource.laguangeElakay('Button', infoSource.languageAPI).saveBank
          )}
        </button>
      </div>
    </div>
  );
};

export default FormBankCountry;
